// src/components/ProjectBuilder/TaskViewPanel.tsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Divider,
  Alert,
  Pagination,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getPrimaryRole } from '../../services/authApi';
import { getCrossProjectTasks, getTaskSummary, TaskFilters, Task, TaskSummary } from '../../services/taskApi';
import TaskKpiStatsBanner from './TaskKpiStatsBanner';
import TaskSummaryCards from './TaskSummaryCards';
import TaskFiltersComponent from './TaskFilters';
import TaskList from './TaskList';
import TaskViewOptions, {
  loadViewOptions,
  saveViewOptions,
  SortBy,
  SortOrder,
  GroupBy,
  Density,
} from './TaskViewOptions';

export interface TaskViewPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
}

export default function TaskViewPanel({ isOpen, onClose, onToggle: _onToggle }: TaskViewPanelProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filters, setFilters] = useState<TaskFilters>({
    page: 1,
    pageSize: 50,
  });
  const [tasks, setTasks] = useState<Task[]>([]);
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 0 });

  // View options state
  const [viewOptions, setViewOptions] = useState(() => loadViewOptions());

  // Debounce filter changes
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const fetchData = useCallback(async (currentFilters: TaskFilters) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch tasks and summary in parallel
      const [tasksResponse] = await Promise.all([
        getCrossProjectTasks(currentFilters),
        getTaskSummary(currentFilters),
      ]);

      setTasks(tasksResponse.tasks);
      setSummary(tasksResponse.summary);
      setPagination(tasksResponse.pagination);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tasks';
      setError(errorMessage);
      console.error('Error loading tasks:', {
        error: err,
        message: errorMessage,
        code: (err as { code?: string }).code,
        filters: currentFilters,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced filter change handler
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      fetchData(filters);
    }, 300);

    setDebounceTimer(timer);

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, fetchData]);

  // Initial load
  useEffect(() => {
    if (isOpen) {
      fetchData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only fetch when panel opens

  const handleFilterChange = (newFilters: TaskFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: newFilters.page || 1 }));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleViewProject = (projectId: number) => {
    navigate(`/execution?projectId=${projectId}`);
    onClose();
  };

  const handleViewTask = (taskId: number, projectId: number) => {
    navigate(`/execution?projectId=${projectId}&taskId=${taskId}`);
    onClose();
  };

  const handleSortChange = (sortBy: SortBy, sortOrder: SortOrder) => {
    setViewOptions(prev => {
      const newOptions = { ...prev, sortBy, sortOrder };
      saveViewOptions(newOptions);
      return newOptions;
    });
  };

  const handleGroupChange = (groupBy: GroupBy) => {
    setViewOptions(prev => {
      const newOptions = { ...prev, groupBy };
      saveViewOptions(newOptions);
      return newOptions;
    });
  };

  const handleDensityChange = (density: Density) => {
    setViewOptions(prev => {
      const newOptions = { ...prev, density };
      saveViewOptions(newOptions);
      return newOptions;
    });
  };

  const userRole = getPrimaryRole(user) || 'Fraport User';

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '40%', md: '500px' },
          minWidth: '500px',
          maxWidth: '800px',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Task View
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* KPI Stats Banner */}
          {summary?.kpiStats && (
            <TaskKpiStatsBanner kpiStats={summary.kpiStats} />
          )}

          {/* Summary Cards */}
          {summary && (
            <TaskSummaryCards
              summary={{
                totalTasks: summary.totalTasks,
                pastDueCount: summary.pastDueCount,
                upcomingCount: summary.upcomingCount,
                inProgressCount: summary.inProgressCount,
                completeCount: summary.completeCount,
              }}
              onFilterChange={(filter) => handleFilterChange({ ...filters, ...filter, page: 1 })}
              activeFilters={filters}
            />
          )}

          {/* Filters */}
          <TaskFiltersComponent
            filters={filters}
            onFilterChange={handleFilterChange}
            userRole={userRole as 'System Admin' | 'Airport Manager' | 'Fraport User' | 'Tenant User (365 Tenant Sense)' | 'Client User'}
          />

          {/* View Options */}
          <TaskViewOptions
            sortBy={viewOptions.sortBy}
            sortOrder={viewOptions.sortOrder}
            groupBy={viewOptions.groupBy}
            density={viewOptions.density}
            onSortChange={handleSortChange}
            onGroupChange={handleGroupChange}
            onDensityChange={handleDensityChange}
            userRole={userRole as 'System Admin' | 'Airport Manager' | 'Fraport User' | 'Tenant User (365 Tenant Sense)' | 'Client User'}
          />

          <Divider sx={{ my: 2 }} />

          {/* Task List */}
          <TaskList
            tasks={tasks}
            loading={loading}
            error={error}
            onViewProject={handleViewProject}
            onViewTask={handleViewTask}
            sortBy={viewOptions.sortBy}
            sortOrder={viewOptions.sortOrder}
            groupBy={viewOptions.groupBy}
            density={viewOptions.density}
            userRole={userRole as 'System Admin' | 'Airport Manager' | 'Fraport User' | 'Tenant User (365 Tenant Sense)' | 'Client User'}
          />
        </Box>

        {/* Footer with Pagination */}
        {pagination.totalPages > 1 && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} tasks
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

